import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

type SessionEntry = {
  id: string;
  repo: string;
  agentName: string;
  title?: string;
  status: "idle" | "working";
  createdAt: string;
};

function useSessionList() {
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/sessions");
      if (res.ok) {
        const data = await res.json();
        setSessions(data.items ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { sessions, loading, refresh };
}

function NewSessionDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [repo, setRepo] = useState("");
  const [agentName, setAgentName] = useState("pr-reviewer");

  const submit = async () => {
    const res = await fetch("/api/sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ repo, agentName }),
    });
    if (res.ok) { setOpen(false); onCreated(); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="mr-1 h-4 w-4" />New session</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New session</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="repo">Repository</Label>
            <Input id="repo" placeholder="owner/repo" value={repo} onChange={e => setRepo(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="agent">Agent</Label>
            <Input id="agent" value={agentName} onChange={e => setAgentName(e.target.value)} />
          </div>
          <Button onClick={submit} disabled={!repo || !agentName}>Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SessionCard({ session, onDelete }: { session: SessionEntry; onDelete: () => void }) {
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    await fetch(`/api/sessions/${session.id}`, { method: "DELETE" });
    onDelete();
  };

  return (
    <Link to={`/sessions/${session.id}`} className="block">
      <div className="flex items-start justify-between rounded-md border border-border bg-card p-4 transition-colors hover:bg-accent/30">
        <div className="flex gap-3">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">{session.title || session.repo}</p>
            <p className="text-xs text-muted-foreground">{session.agentName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={session.status === "working" ? "default" : "secondary"}>
            {session.status}
          </Badge>
          <Button size="icon" variant="ghost" onClick={handleDelete} className="h-7 w-7">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </Link>
  );
}

export default function Sessions() {
  const { sessions, loading, refresh } = useSessionList();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Sessions</h1>
          <p className="text-sm text-muted-foreground">Interactive agent conversations</p>
        </div>
        <NewSessionDialog onCreated={refresh} />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-16 text-center">
          <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No sessions yet. Start one to talk to an agent.</p>
          <NewSessionDialog onCreated={refresh} />
        </div>
      ) : (
        <ScrollArea className="h-[calc(100vh-12rem)]">
          <div className="space-y-2">
            {sessions.map(s => (
              <SessionCard key={s.id} session={s} onDelete={refresh} />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
